
import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import ReCAPTCHA from "react-google-recaptcha";

import {
  CalendarDays,
  Clock3,
  MapPin,
  Ticket,
  CheckCircle2,
  Loader2,
} from "lucide-react";

import { eventConfig } from "./services/eventConfig";
import PaymentInstructions from "../components/events/PaymentInstructions";

const validationSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, "Name must be at least 2 characters")
    .max(80, "Name is too long")
    .required("Name is required"),

  email: Yup.string()
    .email("Enter a valid email address")
    .required("Email is required"),

  phone: Yup.string()
    .matches(
      /^\d{3}-\d{3}-\d{4}$/,
      "Phone must be in format 999-999-9999"
    )
    .required("Phone is required"),


    registrationType: Yup.string()
      .oneOf(["single", "married"])
      .required("Please select a registration type"),

    spouseName: Yup.string().when("registrationType", {
      is: "married",
      then: (schema) =>
        schema
          .min(2, "Spouse's name must be at least 2 characters")
          .required("Spouse's name is required"),
      otherwise: (schema) => schema.notRequired(),
    }),


  paymentMethod: Yup.string()
    .oneOf(["cashapp", "zelle"])
    .required("Please select a payment method"),

  paymentSent: Yup.boolean()
    .oneOf(
      [true],
      "Please confirm that you have sent your payment"
    ),
});

const createReference = () => {
  const bytes = new Uint8Array(6);
  crypto.getRandomValues(bytes);

  return (
    "PBS-" +
    Array.from(bytes, (byte) =>
      byte.toString(16).padStart(2, "0")
    )
      .join("")
      .toUpperCase()
  );
};

const inputClass =
  "w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-gray-900 outline-none transition focus:border-yellow-500 focus:ring-2 focus:ring-yellow-200";

export default function EventRegistration() {
  const recaptchaRef = useRef(null);
  const navigate = useNavigate();
  const [reference] = useState(createReference);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const handleSubmit = async (values, { resetForm }) => {
    setSubmitError("");

    if (!captchaToken) {
      throw new Error("Please complete the reCAPTCHA.");
    }

    const registration =
      eventConfig.registrationTypes[values.registrationType];

    const total = registration.price;

    const payment =
      eventConfig.paymentMethods[values.paymentMethod];

    const message = `
NEW EVENT REGISTRATION

----------------------------------------
EVENT INFORMATION
----------------------------------------

Event: ${eventConfig.title}
Event ID: ${eventConfig.id}
Date: ${eventConfig.date}
Time: ${eventConfig.time}
Location: ${eventConfig.location}

----------------------------------------
ATTENDEE INFORMATION
----------------------------------------

Name: ${values.name}
Email: ${values.email}
Phone: ${values.phone}

----------------------------------------
REGISTRATION
----------------------------------------

Registration Reference: ${reference}
Registration Type: ${registration.label}
Number of Attendees: ${registration.attendees}
Primary Attendee: ${values.name}
Spouse: ${values.registrationType === "married" ? values.spouseName : "N/A"}
Registration Price: $${registration.price.toFixed(2)}
Total: $${total.toFixed(2)}

----------------------------------------
PAYMENT
----------------------------------------

Payment Method: ${payment.label}
Payment Recipient: ${payment.recipient}
Attendee Reports Payment Sent: Yes
Payment Status: PENDING VERIFICATION

----------------------------------------

IMPORTANT:
Payment must be verified manually before
the registration is considered confirmed.
    `;

    const res = await fetch(
      "https://mbmailer.mb-techs.com/mbmailer/form",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": process.env.REACT_APP_MBMAILER_KEY,
        },

        body: JSON.stringify({
          name: values.name,
          appName: "Pure Business Solutions Event Registration",
          business: "Pure Business Solutions",
          email: process.env.REACT_APP_MBMAILER_EMAIL,
          to: eventConfig.registrationEmail,
          subject: `New Event Registration - ${eventConfig.title} - ${reference}`,
          message
        }),
      }
    );

    const result = await res.json();

    if (!res.ok) {
      throw new Error(
        result.error ||
          result.message ||
          "Unable to send registration."
      );
    }

    setSubmitted(true);
    resetForm();
    setCaptchaToken(null);
    recaptchaRef.current?.reset();
  };

  if (submitted) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-gradient-to-br from-[#3E2C20] via-[#5A4332] to-[#A97458] px-4 py-16">
        <div className="w-full max-w-xl rounded-2xl bg-white p-10 text-center shadow-2xl">
          <CheckCircle2
            size={70}
            className="mx-auto mb-6 text-green-600"
          />

          <h1 className="text-3xl font-extrabold text-[#3E2C20]">
            Registration Received!
          </h1>

          <p className="mt-4 text-gray-600">
            Thank you for registering for
            {" "}{eventConfig.title}.
          </p>

          <div className="mt-7 rounded-xl bg-yellow-50 p-6">
            <p className="text-sm font-semibold text-gray-600">
              Your Registration Reference
            </p>

            <p className="mt-2 break-all text-2xl font-extrabold text-[#3E2C20]">
              {reference}
            </p>
          </div>

          <p className="mt-6 text-sm leading-6 text-gray-600">
            Your registration has been received and
            is pending payment verification.
            Please keep your payment receipt.
          </p>
          <button
            onClick={() => navigate("/")}
                className="
                  w-full
                  bg-[#C8A46B]
                  hover:bg-[#B08C57]
                  text-white
                  py-4
                  rounded-full
                  font-semibold
                  shadow-lg
                  hover:shadow-xl
                  transition-all
                  duration-300
                  hover:-translate-y-1
                "
              >
                Return Home
              </button>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-gradient-to-br from-[#3E2C20] via-[#5A4332] to-[#A97458] px-4 py-12 md:py-20">
      <div className="mx-auto max-w-7xl">
        {/* PAGE HEADER */}
        <div className="mb-12 text-center">
          <p className="text-sm font-bold uppercase tracking-[0.25em] text-yellow-300">
            Pure Business Solutions
          </p>

          <h1 className="mt-4 text-4xl font-extrabold text-white md:text-5xl">
            Event Registration
          </h1>

          <p className="mt-4 text-lg text-yellow-100">
            Register for our upcoming event
          </p>
        </div>

        <div className="grid items-start gap-8 lg:grid-cols-12">
          {/* LEFT SIDE - EVENT FLYER */}
          <aside className="lg:col-span-5">
            <div className="overflow-hidden rounded-2xl bg-white shadow-2xl">
              <img
                src={eventConfig.flyer}
                alt={`${eventConfig.title} flyer`}
                className="h-auto w-full object-contain"
              />

              <div className="space-y-5 p-7">
                <h2 className="text-2xl font-extrabold text-[#3E2C20]">
                  {eventConfig.title}
                </h2>

                <p className="text-gray-600">
                  {eventConfig.description}
                </p>

                <div className="space-y-4 border-t border-gray-200 pt-5 text-gray-700">
                  <div className="flex items-center gap-3">
                    <CalendarDays className="text-green-700" />
                    {eventConfig.date}
                  </div>

                  <div className="flex items-center gap-3">
                    <Clock3 className="text-green-700" />
                    {eventConfig.time}
                  </div>

                  <div className="flex items-center gap-3">
                    <MapPin className="text-green-700" />
                    {eventConfig.location}
                  </div>
                </div>


              <div className="rounded-xl bg-yellow-50 p-5">
                <p className="mb-3 font-bold text-[#3E2C20]">
                  Registration Pricing
                </p>

                <div className="flex justify-between py-2 text-[#3E2C20]">
                  <span>Single</span>
                  <strong>
                    ${eventConfig.registrationTypes.single.price.toFixed(2)}
                  </strong>
                </div>

                <div className="flex justify-between py-2 text-[#3E2C20]">
                  <span>Married Couple</span>
                  <strong>
                    ${eventConfig.registrationTypes.married.price.toFixed(2)}
                  </strong>
                </div>
              </div>

              </div>
            </div>
          </aside>

          {/* RIGHT SIDE - FORM */}
          <section className="lg:col-span-7">
            <div className="rounded-2xl bg-white p-6 shadow-2xl md:p-10">
              <div className="mb-8 flex items-center gap-3">
                <Ticket size={30} className="text-green-700" />

                <div>
                  <h2 className="text-2xl font-extrabold text-[#3E2C20]">
                    Register Now
                  </h2>

                  <p className="text-sm text-gray-500">
                    Complete your registration below.
                  </p>
                </div>
              </div>

              <Formik
                initialValues={{
                  name: "",
                  spouseName: "",
                  email: "",
                  phone: "",
                  registrationType: "single",
                  paymentMethod: "cashapp",
                  paymentSent: false,
                }}

                validationSchema={validationSchema}
                onSubmit={async (values, helpers) => {
                  try {
                    await handleSubmit(values, helpers);
                  } catch (error) {
                    console.error(error);
                    setSubmitError(
                      error.message ||
                        "Unable to submit registration."
                    );
                  } finally {
                    helpers.setSubmitting(false);
                  }
                }}
              >
                {({ values, errors, touched, isSubmitting, setFieldValue }) => {
                  const total =
                    eventConfig.registrationTypes[values.registrationType]?.price ?? 0;

                  return (
                    <Form className="space-y-6">
                      {/* NAME */}
                      <div>
                        <label
                          htmlFor="name"
                          className="mb-2 block font-semibold text-gray-700"
                        >
                          Full Name
                        </label>

                        <Field
                          id="name"
                          name="name"
                          type="text"
                          autoComplete="name"
                          className={`${inputClass} ${
                            errors.name && touched.name
                              ? "border-red-500"
                              : ""
                          }`}
                        />

                        <ErrorMessage
                          name="name"
                          component="p"
                          className="mt-1 text-sm text-red-600"
                        />
                      </div>

                      {/* EMAIL */}
                      <div>
                        <label
                          htmlFor="email"
                          className="mb-2 block font-semibold text-gray-700"
                        >
                          Email Address
                        </label>

                        <Field
                          id="email"
                          name="email"
                          type="email"
                          autoComplete="email"
                          className={inputClass}
                        />

                        <ErrorMessage
                          name="email"
                          component="p"
                          className="mt-1 text-sm text-red-600"
                        />
                      </div>

                      {/* PHONE */}
                      <div>
                        <label
                          htmlFor="phone"
                          className="mb-2 block font-semibold text-gray-700"
                        >
                          Phone Number
                        </label>

                        <Field
                          id="phone"
                          name="phone"
                          type="tel"
                          placeholder="999-999-9999"
                          className={inputClass}
                        />

                        <ErrorMessage
                          name="phone"
                          component="p"
                          className="mt-1 text-sm text-red-600"
                        />
                      </div>
                      {/* REGISTRATION TYPE */}
                      <div>
                        <h3 className="mb-4 text-lg font-bold text-[#3E2C20]">
                          Select Registration Type
                        </h3>

                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                          {Object.entries(eventConfig.registrationTypes).map(
                            ([key, registration]) => {
                              const selected = values.registrationType === key;

                              return (
                                <button
                                  key={key}
                                  type="button"
                                  onClick={() => {
                                    setFieldValue("registrationType", key);
                                    setFieldValue("spouseName", "");
                                    setFieldValue("paymentSent", false);
                                  }}
                                  className={`rounded-xl border-2 p-5 text-left transition ${
                                    selected
                                      ? "border-green-700 bg-green-50 shadow-md"
                                      : "border-gray-200 bg-white hover:border-yellow-400"
                                  }`}
                                >
                                  <p className="text-lg font-extrabold text-[#3E2C20]">
                                    {registration.label}
                                  </p>

                                  <p className="mt-1 text-sm text-gray-500">
                                    {registration.description}
                                  </p>

                                  <p className="mt-4 text-3xl font-extrabold text-green-800">
                                    ${registration.price.toFixed(2)}
                                  </p>

                                  <p className="mt-1 text-xs text-gray-500">
                                    {registration.attendees}{" "}
                                    {registration.attendees === 1
                                      ? "attendee"
                                      : "attendees"}
                                  </p>
                                </button>
                              );
                            }
                          )}
                        </div>

                        <ErrorMessage
                          name="registrationType"
                          component="p"
                          className="mt-2 text-sm text-red-600"
                        />
                      </div>

                      {/* SPOUSE NAME - MARRIED ONLY */}
                      {values.registrationType === "married" && (
                        <div>
                          <label
                            htmlFor="spouseName"
                            className="mb-2 block font-semibold text-gray-700"
                          >
                            Spouse's Full Name
                          </label>

                          <Field
                            id="spouseName"
                            name="spouseName"
                            type="text"
                            placeholder="Enter spouse's full name"
                            className={inputClass}
                          />

                          <ErrorMessage
                            name="spouseName"
                            component="p"
                            className="mt-1 text-sm text-red-600"
                          />
                        </div>
                      )}


                      {/* TOTAL */}
                      <div className="flex items-center justify-between rounded-xl bg-[#3E2C20] p-5 text-white">
                        <span className="font-semibold">
                          Registration Total
                        </span>

                        <span className="text-2xl font-extrabold text-yellow-300">
                          ${total.toFixed(2)}
                        </span>
                      </div>

                      {/* PAYMENT SELECTION */}
                      <div>
                        <h3 className="mb-4 text-lg font-bold text-[#3E2C20]">
                          Select Payment Method
                        </h3>

                        <div className="grid grid-cols-2 gap-4">
                          {Object.entries(
                            eventConfig.paymentMethods
                          ).map(([key, payment]) => (
                            <button
                              key={key}
                              type="button"
                              onClick={() => {
                                setFieldValue("paymentMethod", key);
                                setFieldValue("paymentSent", false);
                              }}
                              className={`rounded-xl border-2 px-4 py-5 font-bold transition ${
                                values.paymentMethod === key
                                  ? "border-green-600 bg-green-50 text-green-800"
                                  : "border-gray-200 text-gray-700 hover:border-yellow-400"
                              }`}
                            >
                              {payment.label}
                            </button>
                          ))}
                        </div>

                        <PaymentInstructions
                          method={values.paymentMethod}
                          total={total}
                          reference={reference}
                        />
                      </div>

                      {/* PAYMENT CONFIRMATION */}
                      <div>
                        <label className="flex items-start gap-3 text-sm text-gray-700">
                          <Field
                            type="checkbox"
                            name="paymentSent"
                            className="mt-1 h-5 w-5 accent-green-700"
                          />

                          <span>
                            I have sent my payment and understand
                            that my registration is pending
                            verification by the event organizer.
                          </span>
                        </label>

                        <ErrorMessage
                          name="paymentSent"
                          component="p"
                          className="mt-1 text-sm text-red-600"
                        />
                      </div>

                      {/* RECAPTCHA */}
                      <div className="pt-2">
                        <ReCAPTCHA
                          ref={recaptchaRef}
                          sitekey={process.env.REACT_APP_GOOGLE_ID}
                          onChange={setCaptchaToken}
                          onExpired={() => setCaptchaToken(null)}
                        />
                      </div>

                      {submitError && (
                        <div
                          role="alert"
                          className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700"
                        >
                          {submitError}
                        </div>
                      )}

                      {/* SUBMIT */}
                      <button
                        type="submit"
                        disabled={isSubmitting || !captchaToken}
                        className="flex w-full items-center justify-center gap-3 rounded-xl bg-yellow-400 px-6 py-4 text-lg font-extrabold text-green-950 shadow-lg transition hover:bg-yellow-300 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {isSubmitting ? (
                          <>
                            <Loader2
                              size={20}
                              className="animate-spin"
                            />
                            Submitting...
                          </>
                        ) : (
                          "Submit Registration"
                        )}
                      </button>
                    </Form>
                  );
                }}
              </Formik>
            </div>
          </section>
        </div>
      </div>
    </main>
  );
}
